import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";

const RightDrawer = ({
  open = false,
  onClose,
  title,
  description,
  children,
  width = "480px",
  footer,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(480);

  useEffect(() => {
    if (width) {
      const parsed = parseInt(width);
      if (!isNaN(parsed)) {
        setDrawerWidth(parsed);
      }
    }
  }, [width]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = drawerWidth;

    const handleMouseMove = (moveEvent) => {
      const deltaX = startX - moveEvent.clientX;
      const newWidth = Math.max(320, Math.min(window.innerWidth - 20, startWidth + deltaX));
      setDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [drawerWidth]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[2px] transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className="fixed right-0 top-0 z-50 flex h-screen max-w-full flex-col bg-white shadow-2xl transition-all duration-300 ease-in-out transform"
        style={{
          width: `${drawerWidth}px`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Resize Handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-[#147db5]/20 active:bg-[#147db5]/40 transition-colors z-50"
        />

        {/* Header */}
        {(title || description) ? (
          <div className="flex shrink-0 items-start justify-between border-b border-slate-200 px-6 py-5">
            <div className="min-w-0 pr-4">
              {title && (
                <h2 className="text-lg font-semibold text-slate-900">
                  {title}
                </h2>
              )}

              {description && (
                <p className="mt-1 text-sm leading-5 text-slate-500">
                  {description}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close drawer"
            >
              <X size={19} strokeWidth={1.8} />
            </button>
          </div>
        ) : null}

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {children}
        </div>

        {/* Footer */}

        {footer && (
          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4">
            {footer}
          </div>
        )}
      </aside>
    </>
  );
};

export default RightDrawer;